import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { useMessage } from '../../hooks/useMessage';
import { FieldDef, FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteBulkFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import { Dialog, DialogContent, Typography } from '@mui/material';
import FormsResultsTable from '../FormsResultsTable/FormsResultsTable';
import { transformFormResponses } from '../../utils/forms.utils';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import DataUpdateForm from './DataUpdateForm';
import { useEvents } from '../../hooks/useEvents';
import { useError } from '../../context/error-context';
import AddSingleRowForm from './AddSingleRowForm';
import './ViewAndEditTableForm.scss';

const ViewAndEditTableForm = ({ formId, formAllFields, linkedFormId, formType, skin, formName }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const [ editModalOpen, setEditModalOpen ] = useState(false);
    const [ addNewModalOpen, setAddNewModalOpen ] = useState(false);
    const [ editingResponseId, setEditingResponseId ] = useState('');
    const responseIdsToRemove = useRef<(string | number)[]>([]);
    const { subscribe } = useEvents();
    const { showError } = useError();

    let realFormId = +formId;

    if (linkedFormId) {
        realFormId = linkedFormId;
    }

    useEffect(() => {
        setResultsLoading(true);

        getFormsRows(realFormId, formId)
        .then(response => {
            setResults(response.responses);
        })
        .catch((err) => {
            console.log(err);
        })
        .finally(() => setResultsLoading(false));
    }, [ realFormId, formId ]);

    useEffect(() => {
        return subscribe(`REFRESH_FORMS_${realFormId}`, () => {
            setResultsLoading(true);

            getFormsRows(realFormId, formId)
            .then(response => {
                setResults(response.responses);
            })
            .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to fetch data',
                    showRefresh: true,
                });
            })
            .finally(() => setResultsLoading(false));
        });
    }, [ realFormId, formId, subscribe, showError ]);

    const onBulkDelete = async (responseIdsToRemove: (string | number)[]) => {
        if (responseIdsToRemove.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);

        deleteBulkFormResponse(realFormId, responseIdsToRemove.map(id => +id))
        .then((response) => {
            if (response.deletedCount === 0) {
                showMessage('Could not delete the responses. Please contact to admin.', 'warning', 5000);
            }

            showMessage(`${response.deletedCount} response(s) has been deleted.`, 'success', 5000);
            const filteredResults = results.filter(item => !response.deletedResponseIds.includes(+item.response_id));
            setResults(filteredResults);
        })
        .catch(() => {
            showMessage('Couldn\'t remove the record(s). Please try again later.', 'error', 5000);
        })
        .finally(() => {
            setRemoveLoading(false);
            setConfirmModalOpen(false);
        });
    };

    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);

        deleteBulkFormResponse(realFormId, responseIdsToRemove.current.map(id => +id))
        .then((response) => {
            if (response.deletedCount === 0) {
                showMessage('Could not delete the responses. Please contact to admin.', 'warning', 5000);
            }

            showMessage(`${response.deletedCount} response(s) has been deleted.`, 'success', 5000);
            const filteredResults = results.filter(item => !response.deletedResponseIds.includes(+item.response_id));
            setResults(filteredResults);
        })
        .catch(() => {
            showMessage('Couldn\'t remove the record(s). Please try again later.', 'error', 5000);
        })
        .finally(() => {
            setRemoveLoading(false);
            setConfirmModalOpen(false);
        });
    };

    const onResponseEdit = (responseId: string | number) => {
        setEditingResponseId(responseId + '');
        setEditModalOpen(true);
    };

    const onAfterUpdate = (updatedResult: any) => {
        setResults(results.map(result => {
            if (+result.response_id === updatedResult.response_id) {
                return {
                    ...result,
                    response: updatedResult.data,
                };
            }

            return result;
        }));
    };

    const onDeleteFormResponse = (responseId: string | number) => {
        setConfirmModalOpen(true);
        responseIdsToRemove.current = [ responseId ];
    };

    const shouldShowColumn = (field: FieldDef): boolean => {
        if (skin === 'skin3' && field.type === 'string') {
            return false;
        }

        return !field.hidden;
    };

    const finalFormName = formName || 'Submitted Results';

    return (
        <div className="view-and-edit-table-form">
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px', width: '98%' }}>
                    <div>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={skin === 'skin5' ? {
                                color: '#22312d',
                                fontFamily: 'Georgia, \'Times New Roman\', serif',
                                fontWeight: 700,
                            } : undefined}
                        >
                            {finalFormName}
                        </Typography>
                    </div>

                    {formAllFields.length > 0 ? (
                        <FormsResultsTable
                            config={{ fields: formAllFields.filter(shouldShowColumn) }}
                            formName={finalFormName}
                            data={transformFormResponses(results)}
                            onEdit={onResponseEdit}
                            onDelete={onDeleteFormResponse}
                            onDeleteSelected={onBulkDelete}
                            isRemoveLoading={removeLoading}
                            hasActions
                            isSearchable
                            isSortable
                            skin={skin}
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                    <div className="add-new-container">
                        <Button
                            onClick={() => setAddNewModalOpen(true)}
                            variant="contained"
                            className="add-new"
                            sx={skin === 'skin5' ? {
                                borderRadius: '10px !important',
                                backgroundColor: '#4a967b !important',
                                color: '#ffffff !important',
                                boxShadow: '0 4px 12px rgba(74, 150, 123, 0.22) !important',
                                textTransform: 'none',
                                transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#3f826d !important',
                                    boxShadow: '0 10px 24px rgba(63, 130, 109, 0.38) !important',
                                    transform: 'translateY(-2px)',
                                },
                            } : undefined}
                        >
                            Add New
                        </Button>
                    </div>
                </div>
            )}

            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />

            <MessageContainer/>

            {editModalOpen && editingResponseId && (
                <Dialog
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    maxWidth={false}
                >
                    <DialogContent
                        sx={{
                            minWidth: 300,
                            width: 'fit-content',
                        }}
                    >
                        <DataUpdateForm
                            formId={realFormId + ''}
                            formType={formType}
                            formAllFields={formAllFields}
                            skin={skin}
                            responseId={editingResponseId}
                            onCancel={() => setEditModalOpen(false)}
                            onAfterUpdate={onAfterUpdate}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {addNewModalOpen && (
                <Dialog
                    open={addNewModalOpen}
                    onClose={() => setAddNewModalOpen(false)}
                    maxWidth={false}
                >
                    <DialogContent
                        sx={{
                            minWidth: 300,
                            width: 'fit-content',
                        }}
                    >
                        <AddSingleRowForm
                            formId={realFormId + ''}
                            formType={formType}
                            formAllFields={formAllFields}
                            skin={skin}
                            onSubmitSuccess={() => setAddNewModalOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default ViewAndEditTableForm;